import { Link } from "react-router-dom";
import {
  CheckCircle2,
  Handshake,
  Truck,
  ShieldCheck,
  Users,
  Sparkles,
  Building2,
  Target,
} from "lucide-react";
import { LOGO_URL, LOGO_LIGHT_URL, COMPANY } from "@/lib/brand";

const MISSION_POINTS = [
  "Deliver high-quality injectable formulations through trusted manufacturing partners.",
  "Build long-term relationships with hospitals, distributors, and healthcare professionals.",
  "Ensure consistent product availability through an efficient supply chain.",
  "Grow responsibly while maintaining the highest standards of ethics and professionalism.",
];

const VISION =
  "To become one of India's most trusted hospital-focused pharmaceutical companies by delivering quality products, reliable service, and ethical business practices.";

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Quality",
    body: "Every batch — from trusted partners, audited and dispatch-tested for hospital-grade reliability.",
  },
  {
    icon: Handshake,
    title: "Partnership",
    body: "We treat distributors, hospitals and clinicians as long-term partners, not transactions.",
  },
  {
    icon: Sparkles,
    title: "Ethics",
    body: "Honest pricing, transparent supply and ethical promotion — without exception.",
  },
];

const SUPPLY_PILLARS = [
  "Quality-focused manufacturing partners — selected and audited for hospital-grade output",
  "Cold-chain and ambient logistics built for critical-care timelines",
  "Reliable distributor network across Maharashtra & Madhya Pradesh",
  "Stock visibility and committed availability for hospital tenders",
  "Documentation support: batch certificates, COAs and regulatory paperwork",
];

const THERAPY_AREAS = [
  "Critical Care & Intensive Care",
  "Antibiotics & Anti-Infectives",
  "Anaesthesia",
  "Gastroenterology",
  "Emergency & Acute Care",
  "Hospital & Institutional Healthcare",
];

export default function About() {
  return (
    <div data-testid="page-about" className="bg-[#F8F9F7]">
      {/* HERO */}
      <section className="border-b border-[#E0E5E2] py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="overline text-[#1E8A2E]">
            About Lenax · Est. {COMPANY.founded}
          </p>
          <h1 className="font-serif mt-6 text-5xl sm:text-6xl md:text-7xl tracking-tight leading-[0.95] text-[#121413]">
            About{" "}
            <span className="italic text-[#1E8A2E]">Lenax</span>{" "}
            <span className="text-[#D92D2D]">Pharmaceuticals</span>.
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mt-14 items-start">
            <div className="md:col-span-5 flex md:justify-start">
              <div
                data-testid="about-hero-logo"
                className="relative inline-flex items-center justify-center w-64 h-64 md:w-80 md:h-80"
              >
                <img
                  src={LOGO_LIGHT_URL}
                  alt="Lenax Pharmaceuticals logo"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            <div className="md:col-span-7 space-y-6">
              <p className="text-[#4A524D] leading-relaxed text-base md:text-lg">
                {COMPANY.legal} is a pharmaceutical marketing company
                specializing in hospital and institutional healthcare
                solutions, with a strong focus on critical care and injectable
                formulations.
              </p>
              <p className="text-[#4A524D] leading-relaxed text-base md:text-lg">
                Built on over 20 years of collective pharmaceutical industry
                experience, our team brings deep market knowledge, strong
                professional relationships, and expertise in serving hospitals
                across multiple therapeutic segments. Our experience extends
                beyond pharmaceutical products — we understand hospital
                procurement, distributor management, inventory planning,
                clinical requirements, and the ever-evolving dynamics of the
                critical care market.
              </p>
              <p className="text-[#4A524D] leading-relaxed text-base md:text-lg">
                We provide comprehensive pharmaceutical marketing and
                distribution solutions focused on hospitals, healthcare
                institutions, and critical care services. Our expertise spans
                key therapeutic areas including Critical Care & Intensive
                Care, Antibiotics & Anti-Infectives, Anaesthesia,
                Gastroenterology, Emergency & Acute Care, and Hospital &
                Institutional Healthcare Solutions. By combining quality
                manufacturing partnerships, a robust distributor network, and
                scientific field support, we ensure hospitals receive reliable
                pharmaceutical products with consistent availability and
                dependable service.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* OUR STORY */}
      <section
        data-testid="about-story"
        className="border-b border-[#E0E5E2] py-20 md:py-28 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
            <div className="md:col-span-4">
              <p className="overline text-[#1E8A2E]">Our story</p>
              <h2 className="font-serif mt-4 text-4xl md:text-5xl tracking-tight text-[#121413] leading-tight">
                Two decades of relationships, one brand.
              </h2>
            </div>
            <div className="md:col-span-8 space-y-6">
              <p className="font-serif text-3xl md:text-4xl text-[#121413] leading-snug">
                "Long before the Lenax brand was established, our team had
                already spent more than two decades working with hospitals,
                physicians and distributors."
              </p>
              <p className="text-[#4A524D] leading-relaxed">
                That experience provided valuable insights into clinical
                requirements, hospital purchasing patterns, supply chain
                management, product positioning, and the importance of
                uninterrupted medicine availability.
              </p>
              <p className="text-[#4A524D] leading-relaxed">
                At Lenax, we believe that success in healthcare is built on
                trust, service, and long-term partnerships. We work closely
                with doctors, hospital management, pharmacy teams, purchasing
                departments, and distribution partners to understand their
                needs and deliver dependable pharmaceutical solutions that
                create value for both healthcare providers and patients.
              </p>
              <p className="text-[#4A524D] leading-relaxed">
                Today, Lenax continues to strengthen its presence by
                delivering quality products, responsive customer service,
                efficient distribution support, and ethical business
                practices. As we expand our portfolio and geographical reach,
                our commitment remains unchanged — to become one of India's
                most trusted pharmaceutical marketing companies, recognized
                for excellence in critical care, reliability in service, and
                enduring partnerships across the healthcare ecosystem.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section className="border-b border-[#E0E5E2] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E0E5E2] border border-[#E0E5E2]">
          <div
            data-testid="about-mission"
            className="bg-white p-10 md:p-14"
          >
            <div className="flex items-center gap-3">
              <span className="inline-flex w-10 h-10 items-center justify-center rounded-sm bg-[#FDF1F1] text-[#D92D2D]">
                <Target size={18} />
              </span>
              <p className="overline text-[#D92D2D]">Mission</p>
            </div>
            <p className="font-serif mt-6 text-2xl md:text-3xl text-[#121413] leading-snug">
              Four commitments that shape every decision we make.
            </p>
            <ul className="mt-8 space-y-4">
              {MISSION_POINTS.map((m, i) => (
                <li
                  key={i}
                  data-testid={`mission-item-${i}`}
                  className="flex items-start gap-3 text-sm md:text-base text-[#121413] leading-relaxed"
                >
                  <CheckCircle2
                    size={18}
                    className="text-[#1E8A2E] mt-0.5 shrink-0"
                  />
                  <span>{m}</span>
                </li>
              ))}
            </ul>
          </div>
          <div
            data-testid="about-vision"
            className="bg-white p-10 md:p-14"
          >
            <div className="flex items-center gap-3">
              <span className="inline-flex w-10 h-10 items-center justify-center rounded-sm bg-[#F1F8F2] text-[#1E8A2E]">
                <Sparkles size={18} />
              </span>
              <p className="overline text-[#1E8A2E]">Vision</p>
            </div>
            <p className="font-serif mt-6 text-3xl md:text-4xl text-[#121413] leading-snug">
              {VISION}
            </p>
          </div>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section
        data-testid="about-leadership"
        className="border-b border-[#E0E5E2] py-20 md:py-28 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <p className="overline text-[#1E8A2E]">Leadership</p>
              <h2 className="font-serif mt-4 text-4xl md:text-5xl text-[#121413] tracking-tight leading-tight">
                The people behind Lenax.
              </h2>
              <p className="mt-5 text-sm text-[#4A524D] leading-relaxed max-w-sm">
                Founded in {COMPANY.founded} by two operators who believe
                healthcare partnerships are built — slowly, honestly, and on
                consistent delivery.
              </p>
            </div>
            <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E0E5E2] border border-[#E0E5E2]">
              {COMPANY.founders.map((name, i) => (
                <div
                  key={name}
                  data-testid={`founder-${i}`}
                  className="bg-white p-8 md:p-10"
                >
                  <span className="inline-flex w-12 h-12 items-center justify-center rounded-sm bg-[#F1F3F0] text-[#121413] font-serif text-xl">
                    {name
                      .split(" ")
                      .map((p) => p[0])
                      .slice(0, 2)
                      .join("")}
                  </span>
                  <p className="overline text-[#7A827D] mt-6">
                    Co-founder & Director
                  </p>
                  <h3 className="font-serif text-3xl mt-2 text-[#121413] tracking-tight">
                    {name}
                  </h3>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* QUALITY & SUPPLY */}
      <section className="border-b border-[#E0E5E2] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <p className="overline text-[#1E8A2E]">Quality & supply</p>
              <h2 className="font-serif mt-4 text-4xl md:text-5xl text-[#121413] tracking-tight leading-tight">
                A reliable chain — from partner plant to hospital shelf.
              </h2>
              <p className="mt-6 text-[#4A524D] leading-relaxed max-w-xl">
                We don't take shortcuts on supply. Our manufacturing partners
                are selected for their compliance and consistency, and our
                logistics are built so that the medicine ordered today is on
                the hospital shelf when it's needed.
              </p>
              <ul className="mt-8 space-y-4">
                {SUPPLY_PILLARS.map((s) => (
                  <li
                    key={s}
                    data-testid="standard-item"
                    className="flex items-start gap-3 text-sm text-[#121413]"
                  >
                    <CheckCircle2
                      size={18}
                      className="text-[#1E8A2E] mt-0.5 shrink-0"
                    />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-6 order-1 lg:order-2">
              <div className="relative aspect-[5/6] overflow-hidden rounded-sm">
                <img
                  src="https://images.pexels.com/photos/37650270/pexels-photo-37650270.jpeg"
                  alt="Pharmaceutical injectable manufacturing line"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THERAPY AREAS BENTO */}
      <section className="py-20 md:py-28 border-b border-[#E0E5E2] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="overline text-[#1E8A2E]">Therapeutic focus</p>
              <h2 className="font-serif mt-4 text-4xl md:text-5xl text-[#121413] tracking-tight">
                Where we focus our portfolio.
              </h2>
            </div>
            <Link
              to="/products"
              data-testid="about-products-cta"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#121413] hover:text-[#1E8A2E] transition-colors"
            >
              View full catalog →
            </Link>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E0E5E2] border border-[#E0E5E2]">
            {THERAPY_AREAS.map((t, i) => (
              <div
                key={t}
                data-testid={`therapy-area-${i}`}
                className="bg-white p-8 min-h-[10rem] flex flex-col"
              >
                <span className="font-serif text-3xl text-[#1E8A2E]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="mt-auto font-serif text-xl text-[#121413] leading-snug">
                  {t}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="border-b border-[#E0E5E2] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="overline text-[#1E8A2E]">What we stand for</p>
              <h2 className="font-serif mt-4 text-4xl md:text-5xl text-[#121413] tracking-tight">
                Three values, lived daily.
              </h2>
            </div>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  data-testid={`about-value-${i}`}
                  className="border-t border-[#121413] pt-6"
                >
                  <Icon size={26} className="text-[#D92D2D]" />
                  <h3 className="font-serif text-2xl mt-5 text-[#121413]">
                    {v.title}
                  </h3>
                  <p className="mt-3 text-sm text-[#4A524D] leading-relaxed">
                    {v.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CAPABILITIES BENTO */}
      <section className="py-20 md:py-28 border-b border-[#E0E5E2] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-[#E0E5E2] border border-[#E0E5E2]">
            <div className="md:col-span-8 bg-[#121413] text-white p-10 md:p-14 grain relative">
              <p className="overline text-[#1E8A2E]">The Lenax difference</p>
              <h3 className="font-serif mt-4 text-4xl md:text-5xl leading-tight">
                Built for hospitals — by people who answer the phone.
              </h3>
              <p className="mt-6 text-white/70 max-w-xl leading-relaxed">
                A focused portfolio, a tight supply chain and a small, hands-on
                team that hospitals and distributors can actually reach when
                it matters.
              </p>
            </div>
            <div className="md:col-span-4 bg-white p-10">
              <Building2 size={28} className="text-[#1E8A2E]" />
              <p className="font-serif text-2xl mt-5 text-[#121413] leading-snug">
                Hospital focus
              </p>
              <p className="mt-2 text-sm text-[#4A524D] leading-relaxed">
                Critical care, anaesthesia, antibiotics and GI — built around
                hospital usage patterns.
              </p>
            </div>
            <div className="md:col-span-4 bg-white p-10">
              <Truck size={28} className="text-[#D92D2D]" />
              <p className="font-serif text-2xl mt-5 text-[#121413] leading-snug">
                Reliable supply
              </p>
              <p className="mt-2 text-sm text-[#4A524D] leading-relaxed">
                Distributor network covering Maharashtra & Madhya Pradesh with
                committed availability.
              </p>
            </div>
            <div className="md:col-span-4 bg-white p-10">
              <ShieldCheck size={28} className="text-[#1E8A2E]" />
              <p className="font-serif text-2xl mt-5 text-[#121413] leading-snug">
                Quality partnerships
              </p>
              <p className="mt-2 text-sm text-[#4A524D] leading-relaxed">
                Audited manufacturing partners with batch-level documentation
                and traceability.
              </p>
            </div>
            <div className="md:col-span-4 bg-white p-10">
              <Users size={28} className="text-[#D92D2D]" />
              <p className="font-serif text-2xl mt-5 text-[#121413] leading-snug">
                Personal service
              </p>
              <p className="mt-2 text-sm text-[#4A524D] leading-relaxed">
                Direct lines to the people who can solve your problem — no
                escalation maze.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* LOGO MOMENT */}
      <section className="py-20 md:py-28 bg-[#121413] grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-black overflow-hidden ring-1 ring-white/10">
            <img
              src={LOGO_URL}
              alt="Lenax Pharmaceuticals"
              className="w-24 h-24 object-cover"
            />
          </span>
          <h2 className="font-serif mt-10 text-4xl md:text-6xl text-white tracking-tight">
            {COMPANY.tagline}.
          </h2>
          <p className="mt-6 text-white/70 max-w-2xl mx-auto">
            Whether you're a hospital, a distributor or a clinician — Lenax is
            committed to being a pharma partner you can rely on, consistently.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <a
              href={`mailto:${COMPANY.info_email}`}
              data-testid="about-contact-cta"
              className="inline-flex items-center gap-2 bg-white text-[#121413] px-7 py-4 rounded-sm text-sm font-medium hover:bg-[#1E8A2E] hover:text-white transition-colors duration-300"
            >
              Get in touch
            </a>
            <Link
              to="/career"
              data-testid="about-career-cta"
              className="inline-flex items-center gap-2 border border-white/30 text-white px-7 py-4 rounded-sm text-sm font-medium hover:border-white hover:bg-white hover:text-[#121413] transition-colors duration-300"
            >
              Join the team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
